/**
 * @file resolveAttachmentSizeRefusal.ts
 * @description States why a file is too large to upload, or that it is not.
 * Sole consumer: `useAttachmentUpload`, which runs it per file before reserving
 * a version, so an oversized file produces no `beginAttachmentVersion` call at
 * all rather than a raw `invalid-argument` after a round trip. It lives beside
 * that hook rather than in the panel because no component renders it.
 *
 * The comparison is `>=`, matching the two places that already enforce it —
 * `beginAttachmentVersion` refuses `sizeBytes >= MAX_ATTACHMENT_FILE_BYTES` and
 * `storage.rules` admits only `size < 5 * 1024 * 1024 * 1024`. A file of exactly
 * the cap is refused by all three, which is why the sentence says "smaller
 * than" rather than "at most". Both figures come from the constant through
 * `formatBytes`, so the number the reader is given is the number enforced.
 *
 * The sentence closes on the action that resolves it. The failed transfer row
 * this reaches offers a Retry that cannot help here, so the copy has to point
 * past it.
 */
import { MAX_ATTACHMENT_FILE_BYTES } from '#types';
import { formatBytes } from '../../components/rendering/attachments/formatBytes';

/**
 * @function resolveAttachmentSizeRefusal
 * @description Reports the sentence to show for a file over the per-file cap.
 * @param {{ name: string; size: number }} file - The file about to be uploaded.
 * @returns {string | null} The refusal, or null when the file may be uploaded.
 */
export const resolveAttachmentSizeRefusal = (file: { name: string; size: number }): string | null => {
    if (file.size < MAX_ATTACHMENT_FILE_BYTES) {
        return null;
    }
    return `"${file.name}" is ${formatBytes(file.size)}. Files must be smaller than `
        + `${formatBytes(MAX_ATTACHMENT_FILE_BYTES)}, so it was not uploaded. Attach a smaller file.`;
};
